import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, NavLink, NavLinkProps } from "react-router-dom";
import styled from "styled-components";
import { BaseLink } from "./BaseLink";
import { SelectMenu, Item } from "./SelectMenu";
import {
  legacyListItemStyle,
  legacySelectedTabStyle,
  legacyTabListStyle,
  legacyTabStyle,
  listItemStyle,
  selectedTabStyle,
  tabListStyle,
  tabStyle,
} from "./TabStyles";

import { mediaQueries, MediaQueryWrapper } from "./style";

type LegacyStylesProps = {
  $legacyStyles?: boolean;
};

const TabList = styled.ul<LegacyStylesProps>`
  ${({ $legacyStyles }) => ($legacyStyles ? legacyTabListStyle : tabListStyle)}
`;

const ListItem = styled.li<LegacyStylesProps>`
  ${({ $legacyStyles }) => ($legacyStyles ? legacyListItemStyle : listItemStyle)}
`;

const StyledAuroraLink = styled(BaseLink)<LegacyStylesProps>`
  ${({ $legacyStyles }) => ($legacyStyles ? legacyTabStyle : tabStyle)}
`;

const StyledNavLink = styled(NavLink)<LegacyStylesProps>`
  ${({ $legacyStyles }) => ($legacyStyles ? legacyTabStyle : tabStyle)}

  &.active {
    ${({ $legacyStyles }) => ($legacyStyles ? legacySelectedTabStyle : selectedTabStyle)};
  }
`;

type MobileSelectMenuProps = {
  tabItems: React.ReactNode;
  className?: string;
  mobileDropdownAlignment?: "fixed-bottom" | "fixed-stretch";
};

type SelectItem = {
  label: React.ReactNode;
  value: string;
  meta: {
    type: string | React.JSXElementConstructor<unknown>;
  };
};

const MobileSelectMenu: React.FC<MobileSelectMenuProps> = ({ tabItems, mobileDropdownAlignment, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(0);

  const items = React.useMemo(() => {
    const result: SelectItem[] = [];

    React.Children.forEach(tabItems, (item) => {
      if (!React.isValidElement(item)) {
        return;
      }

      result.push({
        label: item.props.children,
        value: item.props.to,
        meta: { type: item.type },
      });
    });

    return result;
  }, [tabItems]);

  const currentRouteIndex = items.findIndex((item: Item) => item.value === location.pathname);

  useEffect(() => {
    // This makes the dropdown aware of the current route. Without this, the
    // dropdown would always show the first item in the list after a page
    // reload, regardless of the current route.
    if (currentRouteIndex > -1 && tabIndex !== currentRouteIndex) {
      setTabIndex(currentRouteIndex);
    }
  }, []);

  return (
    <SelectMenu
      className={className}
      mobileDropdownAlignment={mobileDropdownAlignment}
      items={items}
      selection={items[tabIndex]}
      setSelection={(item) => {
        setTabIndex(items.findIndex((i) => i.value === item.value));

        if (item.meta && item.meta.type === RouterLinkTab) {
          navigate(item.value);
        } else {
          window.location.href = item.value;
        }
      }}
    />
  );
};

type TabbedNavProps = {
  selectMenuBreakpoint?: MediaQueryWrapper;
  legacyStyles?: boolean;
  className?: string;
  mobileDropdownAlignment?: "fixed-bottom" | "fixed-stretch";
};

/**
 * @deprecated Use `Tabs` instead.
 */
export const TabbedNav: React.FC<TabbedNavProps> = ({ legacyStyles, className, mobileDropdownAlignment = "fixed-stretch", children }) => {
  const isMobile = mediaQueries.xsmallMax.useMatchesBreakpoint();

  if (isMobile) {
    return <MobileSelectMenu mobileDropdownAlignment={mobileDropdownAlignment} className={className} tabItems={children} />;
  } else {
    const tabItems = legacyStyles
      ? React.Children.map(children, (child) => {
          if (React.isValidElement(child) && (child.type === RouterLinkTab || child.type === AuroraLinkTab)) {
            return React.cloneElement(child, { $legacyStyles: true } as any);
          }
        })
      : children;

    return (
      <TabList className={className} $legacyStyles={legacyStyles}>
        {tabItems}
      </TabList>
    );
  }
};

type RouterLinkTabProps = NavLinkProps & {
  $legacyStyles?: boolean;
};

/**
 * @deprecated Use `Tabs` instead.
 */
export const RouterLinkTab: React.FC<RouterLinkTabProps> = (props) => (
  <ListItem $legacyStyles={props.$legacyStyles}>
    <StyledNavLink end {...props} />
  </ListItem>
);

type AuroraLinkProps = LegacyStylesProps & {
  to: string;
};

/**
 * @deprecated Use `Tabs` instead.
 */
export const AuroraLinkTab: React.FC<AuroraLinkProps> = ({ to, $legacyStyles, children }) => {
  return (
    <ListItem $legacyStyles={$legacyStyles}>
      <StyledAuroraLink to={to} $legacyStyles={$legacyStyles} forceExternal target="_self">
        {children}
      </StyledAuroraLink>
    </ListItem>
  );
};
